import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { Toaster } from "./components/ui/sonner";
import ProtectedRoute from "./components/ProtectedRoute";
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import DashboardPage from "./pages/DashboardPage";
import ProgrammesPage from "./pages/ProgrammesPage";
import ProgrammeDetailPage from "./pages/ProgrammeDetailPage";
import CoursesPage from "./pages/CoursesPage";
import CourseDetailPage from "./pages/CourseDetailPage";
import LessonPage from "./pages/LessonPage";
import QuizPage, { CreateQuizPage, EditQuizPage } from "./pages/QuizPage";
import ForumPage from "./pages/ForumPage";
import PaymentPage from "./pages/PaymentPage";
import AdminUsersPage from "./pages/AdminUsersPage";
import AIToolsPage from "./pages/AIToolsPage";
import ProgressPage from "./pages/ProgressPage";
import MessagingPage from "./pages/MessagingPage";
import GalleryAdminPage from "./pages/GalleryAdminPage";
import CareersPage from "./pages/CareersPage";
import LiveMeetingPage from "./pages/LiveMeetingPage";
import MeetingRoomPage from "./pages/MeetingRoomPage";
import CertificatesPage from "./pages/CertificatesPage";
import TranscriptsPage from "./pages/TranscriptsPage";
import AttendancePage from "./pages/AttendancePage";
// Self-service reset removed — admin resets passwords directly
import FreeCoursesPage from "./pages/FreeCoursesPage";
import CommunityPage from "./pages/CommunityPage";
import RoleManagementPage from "./pages/RoleManagementPage";
import AnalyticsPage from "./pages/AnalyticsPage";
import AdminLeadsPage from "./pages/AdminLeadsPage";
import ChangePasswordPage from "./pages/ChangePasswordPage";
import TeamPage from "./pages/TeamPage";
import TestimonialsPage from "./pages/TestimonialsPage";
import GalleryPage from "./pages/GalleryPage";
import ProgrammesCataloguePage from "./pages/ProgrammesCataloguePage";
import PublicProgrammePage from "./pages/PublicProgrammePage";
import VerifyCertificatePage from "./pages/VerifyCertificatePage";
// Section A — Admission letters
import AdmissionLetterTemplatesPage from "./pages/AdmissionLetterTemplatesPage";
import AdminAdmissionLettersPage from "./pages/AdminAdmissionLettersPage";
// Section B — Visual designers
import DocumentDesignerPage from "./pages/DocumentDesignerPage";
// Section C — POD
import PodLandingPage from "./pages/PodLandingPage";
import LeadershipSprintPage from "./pages/LeadershipSprintPage";
import PublicCoursesCataloguePage from "./pages/PublicCoursesCataloguePage";
import PublicCourseDetailPage from "./pages/PublicCourseDetailPage";
import PodSignupPage from "./pages/PodSignupPage";
import PodWebinarsPage from "./pages/PodWebinarsPage";
import PodResourcesPage from "./pages/PodResourcesPage";
import PodAiTemplatesPage from "./pages/PodAiTemplatesPage";
import PodRoundtablePage from "./pages/PodRoundtablePage";
import PodSuccessPage from "./pages/PodSuccessPage";
import PodForumPage from "./pages/PodForumPage";
import PodProfilePage from "./pages/PodProfilePage";
import PodNotificationsPage from "./pages/PodNotificationsPage";
import AdminLoginPage from "./pages/AdminLoginPage";
import AdminModerationPage from "./pages/AdminModerationPage";
import PayNowPage from "./pages/PayNowPage";
import InboxMessagePage from "./pages/InboxMessagePage";
import ApplyProgrammePage from "./pages/ApplyProgrammePage";
import PodDashboardPage from "./pages/PodDashboardPage";
import ApplicationFormPage from "./pages/ApplicationFormPage";
import MyProgrammesPage from "./pages/MyProgrammesPage";
import AdminPage from "./pages/AdminPage";
import PodGuard from "./components/PodGuard";
import AdminPaymentVerificationPage from "./pages/AdminPaymentVerificationPage";
import AdminFeedbackPage from "./pages/AdminFeedbackPage";
import AdminStoragePage from "./pages/AdminStoragePage";
import { usePageTracker } from "./hooks/usePageTracker";

function AuthRedirect({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center min-h-screen bg-white"><div className="animate-pulse text-slate-400 text-sm tracking-widest uppercase">Loading</div></div>;
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
}

function PageTracker({ children }) {
  usePageTracker();
  return children;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <PageTracker>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<AuthRedirect><LoginPage /></AuthRedirect>} />
          <Route path="/admin/login" element={<AdminLoginPage />} />
          <Route path="/register" element={<AuthRedirect><RegisterPage /></AuthRedirect>} />
          {/* Self-service reset removed — admin resets passwords directly */}
          <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
          <Route path="/change-password" element={<ProtectedRoute><ChangePasswordPage /></ProtectedRoute>} />
          <Route path="/programmes" element={<ProtectedRoute><ProgrammesPage /></ProtectedRoute>} />
          <Route path="/programmes/:id" element={<ProtectedRoute><ProgrammeDetailPage /></ProtectedRoute>} />
          <Route path="/courses" element={<ProtectedRoute><CoursesPage /></ProtectedRoute>} />
          <Route path="/courses/:id" element={<ProtectedRoute><CourseDetailPage /></ProtectedRoute>} />
          <Route path="/courses/:courseId/create-quiz" element={<ProtectedRoute roles={["super_admin","admin","instructor"]}><CreateQuizPage /></ProtectedRoute>} />
          <Route path="/lessons/:id" element={<ProtectedRoute><LessonPage /></ProtectedRoute>} />
          <Route path="/quizzes/:id" element={<ProtectedRoute><QuizPage /></ProtectedRoute>} />
          <Route path="/quizzes/:quizId/edit" element={<ProtectedRoute roles={["super_admin","admin","instructor"]}><EditQuizPage /></ProtectedRoute>} />
          <Route path="/forum" element={<ProtectedRoute><ForumPage /></ProtectedRoute>} />
          <Route path="/payments" element={<ProtectedRoute><PaymentPage /></ProtectedRoute>} />
          <Route path="/payment/success" element={<ProtectedRoute><PaymentPage /></ProtectedRoute>} />
          <Route path="/admin/users" element={<ProtectedRoute roles={["super_admin","admin"]}><AdminUsersPage /></ProtectedRoute>} />
          <Route path="/admin/roles" element={<ProtectedRoute roles={["super_admin"]}><RoleManagementPage /></ProtectedRoute>} />
          <Route path="/admin/analytics" element={<ProtectedRoute roles={["super_admin","admin"]}><AnalyticsPage /></ProtectedRoute>} />
          <Route path="/admin/leads" element={<ProtectedRoute roles={["super_admin","admin"]}><AdminLeadsPage /></ProtectedRoute>} />
          <Route path="/admin/moderation" element={<ProtectedRoute roles={["super_admin","admin"]}><AdminModerationPage /></ProtectedRoute>} />
          <Route path="/ai-tools" element={<ProtectedRoute><AIToolsPage /></ProtectedRoute>} />
          <Route path="/progress" element={<ProtectedRoute><ProgressPage /></ProtectedRoute>} />
          <Route path="/messages" element={<ProtectedRoute><MessagingPage /></ProtectedRoute>} />
          <Route path="/meetings" element={<ProtectedRoute><LiveMeetingPage /></ProtectedRoute>} />
          <Route path="/meetings/:meetingId/room" element={<ProtectedRoute><MeetingRoomPage /></ProtectedRoute>} />
          <Route path="/meetings/:meetingId/guest" element={<MeetingRoomPage guest={true} />} />
          <Route path="/certificates" element={<ProtectedRoute><CertificatesPage /></ProtectedRoute>} />
          <Route path="/transcripts" element={<ProtectedRoute><TranscriptsPage /></ProtectedRoute>} />
          <Route path="/attendance" element={<ProtectedRoute><AttendancePage /></ProtectedRoute>} />
          <Route path="/admin/gallery" element={<ProtectedRoute roles={["super_admin","admin"]}><GalleryAdminPage /></ProtectedRoute>} />
          <Route path="/careers" element={<CareersPage />} />
          <Route path="/team" element={<TeamPage />} />
          <Route path="/testimonials" element={<TestimonialsPage />} />
          <Route path="/gallery" element={<GalleryPage />} />
          <Route path="/programmes-catalogue" element={<ProgrammesCataloguePage key="programmes-catalogue" />} />
          <Route path="/p/:idOrCode" element={<PublicProgrammePage />} />
          <Route path="/verify" element={<VerifyCertificatePage />} />
          <Route path="/verify/:certNumber" element={<VerifyCertificatePage />} />
          <Route path="/free-courses" element={<FreeCoursesPage />} />
          <Route path="/community" element={<ProtectedRoute><CommunityPage /></ProtectedRoute>} />

          {/* Section A — Admission Letters */}
          <Route path="/admin/admission-templates" element={<ProtectedRoute roles={["super_admin","admin"]}><AdmissionLetterTemplatesPage /></ProtectedRoute>} />
          <Route path="/admin/admission-letters" element={<ProtectedRoute roles={["super_admin","admin"]}><AdminAdmissionLettersPage /></ProtectedRoute>} />

          {/* Section B — Visual Designers */}
          <Route path="/admin/designers/:kind" element={<ProtectedRoute roles={["super_admin","admin"]}><DocumentDesignerPage /></ProtectedRoute>} />

          {/* Section C — POD (public + authed) */}
          <Route path="/pod" element={<PodLandingPage />} />
          <Route path="/leadership-sprint" element={<LeadershipSprintPage />} />
          <Route path="/courses" element={<PublicCoursesCataloguePage />} />
          <Route path="/courses/public/:id" element={<PublicCourseDetailPage />} />
          <Route path="/pod/signup" element={<PodSignupPage />} />
          <Route path="/pod/dashboard" element={<ProtectedRoute><PodGuard><PodDashboardPage /></PodGuard></ProtectedRoute>} />
          <Route path="/pod/webinars" element={<ProtectedRoute><PodGuard><PodWebinarsPage /></PodGuard></ProtectedRoute>} />
          <Route path="/pod/resources" element={<ProtectedRoute><PodGuard><PodResourcesPage /></PodGuard></ProtectedRoute>} />
          <Route path="/pod/ai-templates" element={<ProtectedRoute><PodGuard><PodAiTemplatesPage /></PodGuard></ProtectedRoute>} />
          <Route path="/pod/roundtable" element={<ProtectedRoute><PodGuard><PodRoundtablePage /></PodGuard></ProtectedRoute>} />
          <Route path="/pod/success" element={<ProtectedRoute><PodGuard><PodSuccessPage /></PodGuard></ProtectedRoute>} />
          <Route path="/pod/forum" element={<ProtectedRoute><PodGuard><PodForumPage /></PodGuard></ProtectedRoute>} />
          <Route path="/pod/profile" element={<ProtectedRoute><PodGuard><PodProfilePage /></PodGuard></ProtectedRoute>} />
          <Route path="/pod/notifications" element={<ProtectedRoute><PodGuard><PodNotificationsPage /></PodGuard></ProtectedRoute>} />
          {/* Safety net: any unmatched /pod/* URL returns the POD shell rather than redirecting to landing. */}
          <Route path="/pod/*" element={<ProtectedRoute><PodGuard><Navigate to="/pod/dashboard" replace /></PodGuard></ProtectedRoute>} />

          {/* Section 8 — Payment Workflow */}
          <Route path="/pay/:programmeId" element={<ProtectedRoute><PayNowPage /></ProtectedRoute>} />
          <Route path="/pay" element={<ProtectedRoute><PayNowPage /></ProtectedRoute>} />
          <Route path="/inbox/:id" element={<ProtectedRoute><InboxMessagePage /></ProtectedRoute>} />
          <Route path="/apply/:id" element={<ApplyProgrammePage />} />
          <Route path="/application/:enrollmentId" element={<ProtectedRoute><ApplicationFormPage /></ProtectedRoute>} />
          <Route path="/my-programmes" element={<ProtectedRoute><MyProgrammesPage /></ProtectedRoute>} />
          <Route path="/admin" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
          <Route path="/admin/payment-verification" element={<Navigate to="/payments?tab=verification" replace />} />
          <Route path="/admin/feedback" element={<ProtectedRoute roles={["super_admin","admin","instructor"]}><AdminFeedbackPage /></ProtectedRoute>} />
          <Route path="/admin/storage" element={<ProtectedRoute roles={["super_admin","admin"]}><AdminStoragePage /></ProtectedRoute>} />

          <Route path="/" element={<Navigate to="/login" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </PageTracker>
      </BrowserRouter>
      <Toaster position="top-right" />
    </AuthProvider>
  );
}

export default App;
